"use client";

import { FooterWithSEO } from "@/components/footer-with-seo";
import { PreferencesBanner } from "@/components/search/preferences-banner";
import { logger } from "@/utils/logger";
import { useEffect } from "react";

export default function SearchError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
	useEffect(() => {
		// Log the error to an error reporting service
		logger.error("Search page error:", error);
	}, [error]);

	return (
		<div className="relative min-h-screen w-full bg-white flex flex-col h-[100dvh]">
			<div className="pt-20 flex-shrink-0">
				<PreferencesBanner />
			</div>
			<main className="flex-1 overflow-y-auto min-h-0">
				<div className="h-full bg-background">
					<div className="mx-auto h-full flex items-center justify-center">
						<div className="text-center p-8 max-w-md">
							<div className="text-6xl mb-6">🏠</div>
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Search Temporarily Unavailable</h2>
							<p className="text-gray-600 mb-6">
								We are experiencing technical difficulties loading property data. This might be due to database
								connectivity issues.
							</p>
							<div className="space-y-4">
								<button
									onClick={reset}
									className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors w-full"
								>
									Try Again
								</button>
								<button
									onClick={() => (window.location.href = "/")}
									className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors w-full"
								>
									Go to Homepage
								</button>
							</div>

							{/* Additional troubleshooting info */}
							<div className="mt-8 text-sm text-gray-500">
								<details>
									<summary className="cursor-pointer hover:text-gray-700">Troubleshooting</summary>
									<div className="mt-2 text-left space-y-2">
										<p>• Check your internet connection</p>
										<p>• Try refreshing the page</p>
										<p>• Clear your browser cache</p>
										<p>• Try a different search location</p>
									</div>
								</details>
							</div>
						</div>
					</div>
				</div>
			</main>
			<div className="hidden md:block">
				<FooterWithSEO />
			</div>

			{/* Hidden error details for debugging */}
			<pre className="opacity-0 text-xs whitespace-pre-wrap absolute bottom-0 left-0">
				{JSON.stringify(
					{
						message: error.message,
						stack: error.stack,
						name: error.name,
						digest: error.digest,
					},
					null,
					2,
				)}
			</pre>
		</div>
	);
}
